import {Component, computed, inject, input, signal, afterNextRender, Injector, ChangeDetectionStrategy} from "@angular/core"
import {conditionsOf, MaterialNode, MaterialSocketUiDefinition, Vec3} from "@cm/material-nodes"
import {MatSelectModule} from "@angular/material/select"
import {ScalarInputSocketComponent} from "@material-editor/components/sockets/scalar-input-socket/scalar-input-socket.component"
import {BooleanInputSocketComponent} from "@material-editor/components/sockets/boolean-input-socket/boolean-input-socket.component"
import {ColorInputSocketComponent} from "@material-editor/components/sockets/color-input-socket/color-input-socket.component"
import {VectorInputSocketComponent} from "@material-editor/components/sockets/vector-input-socket/vector-input-socket.component"
import {Socket} from "@socket-editor/types"
import {Color} from "@cm/graph-v3"
import {SocketConnectionsService} from "@app/socket-editor/services/socket-connections.service"
import {PreviewSceneService} from "@material-editor/services/preview-scene.service"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-material-setting",
    imports: [MatSelectModule, ScalarInputSocketComponent, ColorInputSocketComponent, VectorInputSocketComponent, BooleanInputSocketComponent],
    templateUrl: "./material-setting.component.html",
    styleUrl: "./material-setting.component.scss",
})
export class MaterialSettingComponent {
    public readonly $defaultValue = input<unknown>(undefined, {alias: "defaultValue"})
    public readonly $definition = input.required<MaterialSocketUiDefinition>({alias: "definition"})
    public readonly $disableEditingIfConnected = input(true, {alias: "disableEditingIfConnected"})
    public readonly $material = input.required<MaterialNode>({alias: "material"})
    public readonly $options = computed(() => {
        const definition = this.$definition()
        if (definition.setting && "options" in definition && definition["options"]) {
            return definition["options"] as unknown as {label: string; value: string | number}[]
        }
        return null
    })
    public readonly $socket = computed<Socket>(() => {
        const definition = this.$definition()
        return {
            defaultValue: this.$defaultValue(),
            id: definition.name,
            inputField: !!definition.input,
            label: definition.label,
            paramType: "socket",
            type: "input",
            // TODO: actually evaluate the value
            value: this.$value() ?? this.$defaultValue(),
            valueType: definition.valueType,
        }
    })
    public readonly $value = input.required<unknown>({alias: "value"})
    public readonly $valueType = computed(() => this.$socket().valueType)

    readonly previewSceneService = inject(PreviewSceneService)
    readonly connections = inject(SocketConnectionsService)
    private readonly injector = inject(Injector)

    readonly $connected = signal(false)

    changeValue(newValue: unknown) {
        const material = this.$material()
        const name = this.$definition().name
        material.updateSetting(name, newValue)
        this.previewSceneService.$refresh.next()
        // a setting that gates sockets moves the rows below it, and the connections are drawn to measured positions
        if (material.definition.ui?.items.some((item) => !item.custom && conditionsOf(item.visibleWhen).some((condition) => condition.setting === name))) {
            afterNextRender(() => void this.connections.refreshSocketPositions(material.instanceId), {injector: this.injector})
        }
    }

    readonly $valueAsColor = computed(() => this.$value() as Color)
    readonly $valueAsVec3 = computed(() => this.$value() as Vec3)
    readonly $valueAsNumber = computed(() => this.$value() as number)
    readonly $valueAsBoolean = computed(() => this.$value() as boolean | undefined)
    get valueAsString() {
        return this.$value() as string
    }
}
